import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { apiVerifyOTP, apiRefresh, apiGetMe, apiLogin, apiRegister, apiGetOAuthURL, apiOAuthCallback, apiGetWallet, WalletInfo } from './authApi';

interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'issuer' | 'verifier' | 'holder';
}

interface AuthContextType {
  user: AuthUser | null;
  wallet: WalletInfo | null;
  loading: boolean;
  login: (email: string) => Promise<string | undefined>;
  register: (email: string, name: string) => Promise<string | undefined>;
  verifyOTP: (email: string, code: string) => Promise<void>;
  oauthLogin: (provider: string) => Promise<void>;
  handleOAuthCallback: (provider: string, code: string, state: string) => Promise<void>;
  logout: () => void;
  hasRole: (...roles: string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [wallet, setWallet] = useState<WalletInfo | null>(null);
  const [loading, setLoading] = useState(true);

  // Defined first so it can be referenced by the effects below.
  const logout = useCallback(() => {
    localStorage.removeItem('ida_access_token');
    localStorage.removeItem('ida_refresh_token');
    localStorage.removeItem('ida_oauth_provider');
    setUser(null);
    setWallet(null);
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }, []);

  // Keep a ref to logout so effects can call it without becoming deps on it.
  const logoutRef = useRef(logout);
  useEffect(() => { logoutRef.current = logout; }, [logout]);

  // Track loading state in a ref for the auth:unauthorized handler.
  const loadingRef = useRef(true);
  useEffect(() => { loadingRef.current = loading; }, [loading]);

  // auth:unauthorized event — only logout when the session is genuinely gone.
  useEffect(() => {
    const handler = () => {
      if (!loadingRef.current && !localStorage.getItem('ida_access_token')) {
        logoutRef.current();
      }
    };
    window.addEventListener('auth:unauthorized', handler);
    return () => window.removeEventListener('auth:unauthorized', handler);
  }, []); // runs once — logoutRef always points to latest logout via the ref

  // Restore session on mount — runs ONCE only (empty deps).
  useEffect(() => {
    const restore = async () => {
      const token = localStorage.getItem('ida_access_token');
      if (!token) { setLoading(false); return; }
      try {
        const me = await apiGetMe(token);
        setUser(me);
        try {
          const w = await apiGetWallet(token);
          setWallet(w);
        } catch { /* wallet fetch optional */ }
      } catch {
        const refreshToken = localStorage.getItem('ida_refresh_token');
        if (refreshToken) {
          try {
            const resp = await apiRefresh(refreshToken);
            localStorage.setItem('ida_access_token', resp.accessToken);
            localStorage.setItem('ida_refresh_token', resp.refreshToken);
            setUser(resp.user);
            try {
              const w = await apiGetWallet(resp.accessToken);
              setWallet(w);
            } catch { /* wallet fetch optional */ }
          } catch { logoutRef.current(); }
        } else { logoutRef.current(); }
      }
      setLoading(false);
    };
    restore();
  }, []); // empty — must run exactly once on mount

  const login = useCallback(async (email: string) => {
    return await apiLogin(email);
  }, []);

  const register = useCallback(async (email: string, name: string) => {
    return await apiRegister(email, name);
  }, []);

  const verifyOTP = useCallback(async (email: string, code: string) => {
    const resp = await apiVerifyOTP(email, code);
    localStorage.setItem('ida_access_token', resp.accessToken);
    localStorage.setItem('ida_refresh_token', resp.refreshToken);
    setUser(resp.user);
    if ((resp as any).wallet) {
      setWallet((resp as any).wallet);
    }
  }, []);

  const oauthLogin = useCallback(async (provider: string) => {
    const { authUrl } = await apiGetOAuthURL(provider);
    localStorage.setItem('ida_oauth_provider', provider);
    window.location.href = authUrl;
  }, []);

  const handleOAuthCallback = useCallback(async (provider: string, code: string, state: string) => {
    const redirectUri = `${window.location.origin}/auth/callback`;
    const resp = await apiOAuthCallback(provider, code, state, redirectUri);
    localStorage.setItem('ida_access_token', resp.accessToken);
    localStorage.setItem('ida_refresh_token', resp.refreshToken);
    setUser(resp.user);
    if (resp.wallet) {
      setWallet(resp.wallet);
    }
  }, []);

  const hasRole = useCallback((...roles: string[]) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return roles.includes(user.role);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, wallet, loading, login, register, verifyOTP, oauthLogin, handleOAuthCallback, logout, hasRole }}>
      {children}
    </AuthContext.Provider>
  );
}
